import * as React from 'react';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import CoursePageBreadCrumbSchemaMarkup from 'bundles/seo/components/CoursePageBreadcrumbSchemaMarkup';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import {
  XDP_META_DESC_LENGTH_LIMIT,
  getXdpMetaDescriptionSuffix,
} from 'bundles/unified-description-page-common/constants';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { DomainFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import getSEODescription from 'bundles/unified-description-page-common/utils/getSEODescription';

function getSeoInformation(
  pageData: ConsumerCoursePageFieldsFragment | EnterpriseCoursePageFieldsFragment,
  programSlug?: string
) {
  const { name: title, slug, description, partners, activeSpecialization, primarySpecialization } = pageData;
  const programPath = programSlug ? `/programs${programSlug}` : undefined;
  return {
    title,
    description: getSEODescription(description || '', partners, false),
    canonicalLinkHrefOverride:
      activeSpecialization && primarySpecialization && activeSpecialization.slug !== primarySpecialization.slug
        ? `${programPath ?? ''}/learn/${slug}?specialization=${activeSpecialization.slug}`
        : undefined,
  };
}

type Props = {
  pageData: ConsumerCoursePageFieldsFragment | EnterpriseCoursePageFieldsFragment;
  disableCrawlerIndexing?: boolean;
  paginationProperties?: {
    canonicalLinkHrefOverride?: string;
  };
};

const CoursePageSEO = ({ disableCrawlerIndexing, pageData, paginationProperties }: Props) => {
  const {
    params: { programSlug },
  } = useRouter();

  const { name, domains, metaImgSrc } = pageData;

  const seoData = getSeoInformation(pageData, programSlug);

  const domain: DomainFragment | undefined = domains?.[0];

  return (
    <>
      {!user.isAuthenticatedUser() && <CoursePageBreadCrumbSchemaMarkup domain={domain} />}
      <CourseraMetatags
        metaNameAndProperties={{
          title: seoData.title,
          description: seoData.description,
          ogAndTwitterTitle: name,
          absoluteImageHref: metaImgSrc ?? undefined,
          twitterCardWithLargeImage: true,
          disableCourseraDescriptionSuffix: true,
          descriptionLengthLimit: XDP_META_DESC_LENGTH_LIMIT - getXdpMetaDescriptionSuffix().length,
          descriptionSuffix: getXdpMetaDescriptionSuffix(),
          disableCrawlerIndexing,
        }}
        {...(disableCrawlerIndexing && { hrefLangOverrides: () => [] })}
        paginationProperties={paginationProperties}
      />
    </>
  );
};

export default CoursePageSEO;
